import { Injectable } from '@angular/core';
import { catchError, combineLatest, map, Observable, of, switchMap, take } from 'rxjs';
import { Agent, PolicyTypes, PolicyVersionMap } from '@ag-common-lib/public-api';
import { PolicyService } from './policy.service';
import { PolicySignersService } from './policy-signers.service';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { shareReplay } from 'rxjs/operators';
import { getMaxVersion, isVersionGreater } from 'ag-common-svc/lib/utils/last-signing-versions-by-agreement-type.util';
import { PolicySigners, PolicySignersKeys } from '@ag-common-lib/lib/models/utils/policy-signers.model';

@Injectable({ providedIn: 'root' })
export class PolicyHelperService {
  constructor(
    private policyService: PolicyService,
    private policySignersService: PolicySignersService,
  ) {
  }

  /**
   * Determines if the user agreements acceptance modal should be displayed
   * @param loggedInAgent$ Observable of the logged-in agent
   * @returns Observable<boolean> true if modal should be shown
   */
  getDisplayUserAgreementsModal$(loggedInAgent$: Observable<Agent | null>): Observable<boolean> {
    return combineLatest([loggedInAgent$, this.policyService.getLastPublishedDocuments()]).pipe(
      switchMap(([agent, publishedPolicies]) => {
        if (!publishedPolicies?.length) {
          return of(false);
        }
        // show for logged-in agents with a valid dbId
        const agentDbId = agent?.[BaseModelKeys.dbId];
        if (!agentDbId) {
          return of(false);
        }

        // All policy types and ensure they emit at least once
        const policyTypeObservables = Object.values(PolicyTypes).map(policyType =>
          this.policyService.getPolicyVersionsByType(policyType).pipe(
            take(1),
            catchError(() => of([] as PolicyVersionMap[])),
            shareReplay(1),
          ),
        );

        const allPolicyVersions$ = combineLatest(policyTypeObservables).pipe(
          map((policyVersionsArray: PolicyVersionMap[][]) => {
            const policyVersionsByType: Record<PolicyTypes, PolicyVersionMap[]> = {} as Record<
              PolicyTypes,
              PolicyVersionMap[]
            >;
            Object.values(PolicyTypes).forEach((policyType, index) => {
              const policyVersions = policyVersionsArray[index].sort(this.compareVersionsDesc);
              policyVersionsByType[policyType] = policyVersions || [];
            });
            return policyVersionsByType;
          }),
          shareReplay(1),
        );

        // Batch fetch all signed versions for all types at once
        const allSignedVersions$ = this.policySignersService.getSignersByAgentDbId(agentDbId).pipe(
          map((signers: PolicySigners[]) => this.mapSignersToSignedVersionsByType(signers)),
          catchError(() => this.getEmptySignedVersionsByType()),
          shareReplay(1),
        );

        return combineLatest([allPolicyVersions$, allSignedVersions$]).pipe(
          map(([policyVersionsByType, signedVersionsByType]) => {
            const shouldShow = this.shouldDisplayModal(policyVersionsByType, signedVersionsByType);
            // console.log('Acceptance modal should be visible ', shouldShow);
            return shouldShow;
          }),
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
      catchError(() => of(false)),
    );
  }

  /**
   * Maps signers array to signed versions by policy type
   * @param signers Array of policy signers
   * @returns Record mapping policy types to their last signed version
   * {termsOfService: '5.1.0', privacyPolicy: '5.2.0'}
   */
  private mapSignersToSignedVersionsByType(signers: PolicySigners[]): Record<PolicyTypes, string | null> {
    const signedVersionsByType: Record<PolicyTypes, string | null> = {} as Record<PolicyTypes, string | null>;

    // Initialize all types to null
    Object.values(PolicyTypes).forEach(type => {
      signedVersionsByType[type] = null;
    });

    // Filter out null signers and group by policy type
    const signersByType = new Map<PolicyTypes, PolicySigners[]>();
    signers
      .filter((signer): signer is PolicySigners => signer != null)
      .forEach(signer => {
        const type = signer?.[PolicySignersKeys.policyType];
        if (type) {
          if (!signersByType.has(type)) {
            signersByType.set(type, []);
          }
          signersByType.get(type)!.push(signer);
        }
      });

    // Calculate max version for each type
    signersByType.forEach((typeSigners, type) => {
      const versions = typeSigners
        .map(signer => signer?.[PolicySignersKeys.policyVersion])
        .filter((version): version is string => !!version);

      if (versions.length > 0) {
        signedVersionsByType[type] = getMaxVersion(versions) ?? null;
      }
    });

    return signedVersionsByType;
  }

  /**
   * Returns empty signed versions record with all types set to null
   * @returns Record mapping policy types to null
   */
  private getEmptySignedVersionsByType(): Observable<Record<PolicyTypes, string | null>> {
    return of(
      Object.values(PolicyTypes).reduce((acc, type) => {
        acc[type] = null;
        return acc;
      }, {} as Record<PolicyTypes, string | null>),
    );
  }

  /**
   * Determines if the modal should be displayed based on policy versions and signed versions
   * @param policyVersionsByType Policy versions grouped by type
   * @param signedVersionsByType Last signed versions grouped by type
   * @returns true if modal should be shown
   */
  private shouldDisplayModal(
    policyVersionsByType: Record<PolicyTypes, PolicyVersionMap[]>,
    signedVersionsByType: Record<PolicyTypes, string | null>,
  ): boolean {
    console.log({policyVersionsByType, signedVersionsByType});
    return Object.values(PolicyTypes).some(policyType => {
      const policyVersions = policyVersionsByType?.[policyType] ?? null;
      const lastSignedVersion = signedVersionsByType?.[policyType] ?? null;
      if (!policyVersions?.length) {
        return false;
      }

      // Get published version (first in the array as it's sorted by updated_date desc)
      const publishedVersion = Object.keys(policyVersions[0])[0];
      if (!publishedVersion) {
        return false;
      }

      // Get step number
      const step = this.getStepNumber(policyVersions, lastSignedVersion);

      // Determine if modal should be shown
      return this.shouldShowModalByStep(step, policyVersions);
    });
  }

  /**
   * Get step number by type from published version and last signed version
   * @param policyVersions Array of policy versions ordered by versions (desc)
   * @param lastSignedVersion The last signed version or null
   * @returns number The step number (0 = same, 1 = 1 step, >1 = more than 1 step)
   */
  private getStepNumber(policyVersions: PolicyVersionMap[], lastSignedVersion: string | null): number {
    if (!policyVersions?.length) {
      return 0;
    }

    // Get published version (first in the array as it's sorted by version desc)
    const publishedVersion = Object.keys(policyVersions[0])[0];
    if (!publishedVersion) {
      return 0;
    }

    // No signed version
    if (!lastSignedVersion) {
      return policyVersions.length;
    }

    // Same version
    if (publishedVersion === lastSignedVersion) {
      return 0;
    }

    // If signed version is greater than published, return 0
    if (isVersionGreater(lastSignedVersion, publishedVersion)) {
      return 0;
    }

    // Find the index of the last signed version
    const signedVersionIndex = policyVersions.findIndex(versionMap => {
      const version = Object.keys(versionMap)[0];
      return version === lastSignedVersion;
    });

    // If signed version not found in the list, return the total number of versions
    if (signedVersionIndex === -1) {
      return policyVersions.length;
    }

    // Return the number of steps between signed and published (published is at index 0)
    return signedVersionIndex;
  }

  /**
   * Determine if modal should be shown based on step and silent flags
   * Show modal if:
   * 1) step = 1 and silent = true for the published version
   * 2) step > 1 and there is at least one silent = true between signed and published versions
   * @param step The step number (0 = same, 1 = 1 step, >1 = more than 1 step)
   * @param policyVersions Array of policy versions ordered by version (desc)
   * @returns boolean true if modal should be shown
   */
  private shouldShowModalByStep(
    step: number,
    policyVersions: PolicyVersionMap[],
  ): boolean {
    // No step difference, don't show
    if (step === 0) {
      return false;
    }

    // Step = 1, show if silent = true for the published version (index 0)
    if (step === 1) {
      const publishedVersionMap = policyVersions[0];
      if (!publishedVersionMap) {
        return false;
      }
      const publishedVersion = Object.keys(publishedVersionMap)[0];
      return publishedVersionMap[publishedVersion] === true;
    }

    // Step > 1, show if there is at least one silent = true between signed and published
    // Published is at index 0, signed is at index step
    // if lastVersion for all
    const lastPublishedVersionMap = policyVersions[0];
    const lastVersion = Object.keys(lastPublishedVersionMap)[0];
    const value = lastPublishedVersionMap[lastVersion];
    if (value) {
      return true;
    }
    // Versions between are at indices 1 to step-1 (excluding both endpoints)
    const versionsBetween = policyVersions.slice(1, step);
    return versionsBetween.some(versionMap => {
      const version = Object.keys(versionMap)[0];
      return versionMap[version] === true;
    });
  }

  /**
   * Compares two policy version maps and returns -1 if a is less than b,
   * 1 if a is greater than b, and 0 if they are equal.
   * @param a The first policy version map to compare
   * @param b The second policy version map to compare
   * @returns -1 if a is less than b, 1 if a is greater than b, and 0 if they are equal
   */
  private compareVersionsDesc(a: any, b: any): number {
    const versionA = Object.keys(a)[0];
    const versionB = Object.keys(b)[0];

    const partsA = versionA.split('.').map(Number);
    const partsB = versionB.split('.').map(Number);

    for (let i = 0; i < 3; i++) {
      if (partsA[i] > partsB[i]) {
        return -1;
      }
      if (partsA[i] < partsB[i]) {
        return 1;
      }
    }
    return 0;
  }
}
